import { Text, View } from 'react-native'
import MarkdownDisplay, {
  ASTNode,
  hasParents,
  MarkdownIt,
  MarkdownProps,
  RenderRules,
} from 'react-native-markdown-display'

import Typography from '@ui/typography'
import { getStrong, TypographyVariant } from '@ui/typography/types'

import spanColorPlugin from './spanColorPlugin'
import { defaultMarkdownStyles } from './styles'

const markdownIt = MarkdownIt({ typographer: true }).use(spanColorPlugin)

type HeadingElement =
  | 'heading1'
  | 'heading2'
  | 'heading3'
  | 'heading4'
  | 'heading5'
  | 'heading6'

const defaultHeadingVariants = {
  heading1: 'XLB',
  heading2: 'LGB',
  heading3: 'MDB',
  heading4: 'SMB',
  heading5: 'XSB',
  heading6: 'XS',
} as const satisfies Record<HeadingElement, TypographyVariant>

export type MarkdownStyle = MarkdownProps['style']

type Props = {
  /** Markdown source, e.g. straight from a translation string. */
  children: string
  /** Typography variant for body text. */
  variant?: TypographyVariant
  /** Typography variants per heading level, merged over the defaults (H1–H6). */
  headingVariants?: Partial<Record<HeadingElement, TypographyVariant>>
  /**
   * Per-element style overrides, same keys as react-native-markdown-display
   * (`paragraph`, `bullet_list_icon`, `blockquote`, ...).
   * See `defaultMarkdownStyles` for the built-in layout defaults.
   */
  style?: MarkdownStyle
  onLinkPress?: MarkdownProps['onLinkPress']
}

/**
 * Renders a markdown string with app typography. Every text node is wrapped
 * in `Typography`, headings map to heading variants, `**bold**` maps to the
 * bold variant of the current size, and `<span color="#00ff00">...</span>`
 * renders inline colored text (see `spanColorPlugin`).
 *
 * Body / list markers use `text-desc`; bold uses `text-text` (black) unless
 * nested inside a color span.
 *
 * Library: `react-native-markdown-display` (CommonMark via markdown-it,
 * native View/Text — works on iOS and web through react-native-web).
 */
export default function Markdown({
  children,
  variant = 'MD',
  headingVariants,
  style,
  onLinkPress,
}: Props) {
  const headings = { ...defaultHeadingVariants, ...headingVariants }

  const getInlineVariant = (parents: ASTNode[]): TypographyVariant => {
    const heading = parents.find((parent) => parent.type in headings)
    if (!heading) {
      return variant
    }
    return headings[heading.type as HeadingElement]
  }

  // Nested Typography (e.g. strong inside span_color) applies `text-text`
  // and would otherwise override the parent's color — inherit it explicitly.
  const getSpanColor = (parents: ASTNode[]): string | undefined => {
    const span = parents.find((parent) => parent.type === 'span_color')
    return span?.attributes?.color
  }

  const rules: RenderRules = {
    textgroup: (node, nodeChildren, parents) => (
      <Typography
        key={node.key}
        variant={getInlineVariant(parents)}
        className="text-desc"
      >
        {nodeChildren}
      </Typography>
    ),
    strong: (node, nodeChildren, parents) => (
      <Typography
        key={node.key}
        variant={getStrong(getInlineVariant(parents))}
        // Default body is text-desc; bold stays black unless inside a color span.
        className="text-black"
        color={getSpanColor(parents)}
      >
        {nodeChildren}
      </Typography>
    ),
    em: (node, nodeChildren) => (
      <Text key={node.key} style={{ fontStyle: 'italic' }}>
        {nodeChildren}
      </Text>
    ),
    span_color: (node, nodeChildren, parents) => (
      <Typography
        key={node.key}
        variant={getInlineVariant(parents)}
        color={node.attributes.color}
      >
        {nodeChildren}
      </Typography>
    ),
    // Reimplemented from the library defaults so the bullet / number icons
    // are rendered with Typography instead of the platform default font.
    list_item: (node, nodeChildren, parents, styles) => {
      const iconVariant = getInlineVariant(parents)

      if (hasParents(parents, 'bullet_list')) {
        return (
          <View key={node.key} style={styles._VIEW_SAFE_list_item}>
            <Typography
              variant={iconVariant}
              className="text-desc"
              style={styles.bullet_list_icon}
              accessible={false}
            >
              {'\u2022'}
            </Typography>
            <View style={styles._VIEW_SAFE_bullet_list_content}>
              {nodeChildren}
            </View>
          </View>
        )
      }

      if (hasParents(parents, 'ordered_list')) {
        const orderedList = parents.find(
          (parent) => parent.type === 'ordered_list'
        )
        const start = orderedList?.attributes?.start
        const number = (typeof start === 'number' ? start : 1) + node.index
        return (
          <View key={node.key} style={styles._VIEW_SAFE_list_item}>
            <Typography
              variant={iconVariant}
              className="text-desc"
              style={styles.ordered_list_icon}
            >
              {`${number}${node.markup}`}
            </Typography>
            <View style={styles._VIEW_SAFE_ordered_list_content}>
              {nodeChildren}
            </View>
          </View>
        )
      }

      return (
        <View key={node.key} style={styles._VIEW_SAFE_list_item}>
          {nodeChildren}
        </View>
      )
    },
  }

  return (
    <MarkdownDisplay
      markdownit={markdownIt}
      rules={rules}
      style={{ ...defaultMarkdownStyles, ...style }}
      onLinkPress={onLinkPress}
    >
      {children}
    </MarkdownDisplay>
  )
}
